<template>
  <div ref="root" class="modern-shell">
    <ModernTopbar :identity="identity" :preference-state="preferenceState" :notification-state="notificationState"
      :fullscreen-state="fullscreenState" :chat-state="chatState" @account-opened="refreshNotifications"
      @mark-read="markNotificationsRead" @navigate="navigate" @theme="changeTheme" @fullscreen="toggleFullscreen"
      @chat="openChat" @classic="switchToClassic" @logout="logout" />
    <main class="modern-shell__content"><slot /></main>
  </div>
</template>

<script>
import ModernTopbar from '@/shells/modern/ModernTopbar.vue';
import { modernAccountIdentity } from '@/shells/models/modernAccount';
import { interfacePreferencesController } from '@/shells/services/interfacePreferencesController';
import { modernNotificationsController } from '@/shells/services/modernNotificationsController';
import { MODERN_THEME_KEY, ModernThemeController } from '@/shells/services/modernThemeController';
import { FullscreenController } from '@/shells/services/fullscreenController';
import { modernChatService } from '@/shells/services/modernChatService';
import { modernAccountRepository } from '@/shells/api/modernAccountRepository';

export default {
  name: 'ModernShell',
  components: { ModernTopbar },
  provide() { return { [MODERN_THEME_KEY]: this.themeController.state }; },
  data() {
    const uid = this.$store.state.user_id || (this.$store.state.user && this.$store.state.user.uid) || 0;
    return {
      uid,
      preferenceState: interfacePreferencesController.stateFor(uid),
      notificationState: modernNotificationsController.state,
      chatState: modernChatService.state,
      fullscreenController: new FullscreenController(),
      themeController: new ModernThemeController()
    };
  },
  computed: {
    identity() { return modernAccountIdentity(this.$store.state.user || {}); },
    fullscreenState() { return this.fullscreenController.state; },
    themeMode() { return this.preferenceState.preferences.themeMode; }
  },
  watch: {
    themeMode(mode) { this.themeController.setMode(mode); }
  },
  mounted() {
    this.themeController.mount(this.$refs.root, this.themeMode);
    this.fullscreenController.mount(this.$refs.root);
    interfacePreferencesController.load(this.uid).catch(() => {});
    modernNotificationsController.load().catch(() => {});
    modernChatService.checkAvailability((this.$store.state.user || {}).roles);
  },
  beforeDestroy() {
    this.themeController.unmount();
    this.fullscreenController.unmount();
  },
  methods: {
    refreshNotifications() { modernNotificationsController.load().catch(() => {}); },
    async markNotificationsRead() {
      try { await modernNotificationsController.markAllRead(); }
      catch (error) { this.feedback('error', 'No se pudieron marcar los avisos como leídos.'); }
    },
    navigate(routeName) { this.$router.push({ name: routeName }); },
    async changeTheme(themeMode) {
      try { await interfacePreferencesController.update(this.uid, { themeMode }); }
      catch (error) { this.feedback('error', 'No se pudo guardar el tema.'); }
    },
    async toggleFullscreen() {
      try { await this.fullscreenController.toggle(); }
      catch (error) { this.feedback('error', 'No se pudo cambiar el modo de pantalla.'); }
    },
    async openChat() {
      try { await modernChatService.open(); }
      catch (error) { this.feedback('error', 'No se pudo abrir el chat.'); }
    },
    async switchToClassic() {
      try {
        await interfacePreferencesController.update(this.uid, { shell: 'legacy' });
        await this.$router.push({ name: 'Stockchart' });
      } catch (error) { this.feedback('error', 'No se pudo cambiar a la versión clásica.'); }
    },
    async logout() {
      try {
        await modernAccountRepository.logout();
        localStorage.removeItem('user_access_token');
        window.location.reload();
      } catch (error) { this.feedback('error', 'No se pudo cerrar la sesión.'); }
    },
    feedback(type, message) {
      if (this.$vueOnToast && this.$vueOnToast.pop) this.$vueOnToast.pop(type, message);
    }
  }
};
</script>

<style lang="scss">
.modern-shell {
  --modern-layer-modal: 10040;
  --modern-layer-popover: 10050;
  --modern-layer-feedback: 10060;
  --modern-shell-header-height: 62px;
  --modern-bg: #f6f8fb;
  --modern-surface: #ffffff;
  --modern-text: #15202b;
  --modern-muted: #657181;
  --modern-border: #dfe5ec;
  --modern-accent: #008db5;
  --modern-accent-soft: #e6f7fb;
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--modern-bg);
  color: var(--modern-text);
  color-scheme: light;
}
.modern-shell.modern-theme-dark {
  --modern-bg: #10131a;
  --modern-surface: #191e28;
  --modern-text: #f5f7fa;
  --modern-muted: #aab4c2;
  --modern-border: #303846;
  --modern-accent: #51c5e5;
  --modern-accent-soft: #173642;
  color-scheme: dark;
}
.modern-shell__content {
  min-width: 0;
  min-height: calc(100vh - var(--modern-shell-header-height));
  min-height: calc(100dvh - var(--modern-shell-header-height));
}
.modern-shell .charting-workspace {
  min-height: calc(100vh - var(--modern-shell-header-height));
  min-height: calc(100dvh - var(--modern-shell-header-height));
}
@media (min-width: 1100px) {
  .modern-shell {
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }
  .modern-shell__content {
    height: calc(100vh - var(--modern-shell-header-height));
    height: calc(100dvh - var(--modern-shell-header-height));
    min-height: 0;
    overflow: hidden;
  }
  .modern-shell .charting-workspace {
    height: 100%;
    min-height: 0;
  }
}
.modern-topbar {
  position: sticky; top: 0; z-index: 10020; height: var(--modern-shell-header-height); box-sizing: border-box;
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 24px;
  width: 100%; padding: 0 20px; border-bottom: 1px solid var(--modern-border); background: color-mix(in srgb, var(--modern-surface) 94%, transparent);
  box-shadow: 0 2px 10px rgba(15, 23, 42, .05); font-family: 'Open Sans', sans-serif;
}
.modern-topbar button { font: inherit; color: inherit; }
.modern-topbar__brand { display: flex; align-items: center; gap: 9px; color: var(--modern-text); text-decoration: none; font-weight: 700; }
.modern-topbar__brand img { width: 31px; height: 31px; object-fit: contain; }
.modern-primary-navigation { display: flex; align-items: stretch; justify-content: center; min-width: 0; height: 100%; }
.modern-primary-navigation__item { position: relative; display: flex; align-items: center; gap: 5px; padding: 0 13px; border: 0; background: none; color: var(--modern-muted); text-decoration: none; white-space: nowrap; font-size: 14px; }
.modern-primary-navigation__item span { display: none; }
.modern-primary-navigation__item.is-active { color: var(--modern-text); font-weight: 700; }
.modern-primary-navigation__item.is-active::after { content: ''; position: absolute; right: 13px; bottom: 0; left: 13px; height: 3px; border-radius: 3px 3px 0 0; background: var(--modern-accent); }
.modern-primary-navigation__item.is-disabled { cursor: not-allowed; opacity: .62; }
.modern-primary-navigation__more { display: none; position: relative; align-items: center; }
.modern-primary-navigation__more > button, .modern-topbar__mobile-toggle { border: 0; background: none; cursor: pointer; }
.modern-primary-navigation__more-menu { position: absolute; top: 50px; right: 0; width: 230px; padding: 8px; border: 1px solid var(--modern-border); border-radius: 12px; background: var(--modern-surface); box-shadow: 0 14px 35px rgba(15,23,42,.16); }
.modern-primary-navigation__more-menu button { display: flex; justify-content: space-between; width: 100%; padding: 10px; border: 0; background: none; color: var(--modern-muted); }
.modern-primary-navigation__more-menu small { font-size: 10px; }
.modern-account-trigger { position: relative; width: 42px; height: 42px; padding: 0; border: 1px solid var(--modern-border); border-radius: 50%; background: var(--modern-surface); cursor: pointer; }
.modern-account-trigger__avatar, .modern-account-menu__avatar { display: grid; place-items: center; border-radius: 50%; background: linear-gradient(145deg, #087fa2, #44bbdd); color: white; font-weight: 800; }
.modern-account-trigger__avatar { width: 100%; height: 100%; }
.modern-account-trigger__badge { position: absolute; top: -5px; right: -6px; display: grid; place-items: center; min-width: 19px; height: 19px; padding: 0 4px; border: 2px solid var(--modern-surface); border-radius: 10px; background: #d52f4c; color: white; font-size: 10px; font-weight: 800; box-sizing: border-box; }
.modern-account-menu { position: absolute; top: calc(100% + 8px); right: 16px; z-index: 10030; width: min(370px, calc(100vw - 24px)); max-height: calc(100vh - var(--modern-shell-header-height) - 20px); max-height: calc(100dvh - var(--modern-shell-header-height) - 20px); overflow-y: auto; box-sizing: border-box; padding: 16px; border: 1px solid var(--modern-border); border-radius: 16px; background: var(--modern-surface); box-shadow: 0 22px 60px rgba(15,23,42,.22); color: var(--modern-text); }
.modern-account-menu__identity { display: flex; align-items: center; gap: 12px; padding: 2px 2px 14px; }
.modern-account-menu__avatar { width: 44px; height: 44px; flex: 0 0 auto; }
.modern-account-menu__identity div { display: grid; gap: 2px; }
.modern-account-menu__identity span { color: var(--modern-muted); font-size: 12px; }
.modern-account-menu__section, .modern-notifications-preview { padding: 12px 0; border-top: 1px solid var(--modern-border); }
.modern-account-menu__section > button { display: flex; align-items: center; gap: 12px; width: 100%; padding: 9px 8px; border: 0; border-radius: 8px; background: none; text-align: left; cursor: pointer; }
.modern-account-menu__section > button:hover, .modern-account-menu__section > button:focus-visible { background: var(--modern-accent-soft); }
.modern-account-menu h3 { margin: 0 0 8px; color: var(--modern-muted); font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.modern-account-menu__section-heading { display: flex; justify-content: space-between; gap: 12px; }
.modern-account-menu__section-heading span { color: var(--modern-accent); font-size: 11px; font-weight: 700; }
.modern-notifications-preview ul { display: grid; gap: 4px; margin: 0; padding: 0; list-style: none; }
.modern-notifications-preview li { display: grid; gap: 2px; padding: 7px 8px 7px 13px; border-radius: 8px; color: var(--modern-muted); font-size: 13px; }
.modern-notifications-preview li.is-unread { position: relative; color: var(--modern-text); font-weight: 600; background: var(--modern-accent-soft); }
.modern-notifications-preview li.is-unread::before { content: ''; position: absolute; top: 13px; left: 5px; width: 5px; height: 5px; border-radius: 50%; background: var(--modern-accent); }
.modern-notifications-preview time { color: var(--modern-muted); font-size: 10px; font-weight: 400; }
.modern-account-menu__text-action { margin: 6px 0 0; padding: 5px 8px; border: 0; background: none; color: var(--modern-accent) !important; cursor: pointer; font-size: 12px !important; }
.modern-account-menu__themes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; margin-bottom: 7px; }
.modern-account-menu__themes button { padding: 7px 5px; border: 1px solid var(--modern-border); border-radius: 8px; background: none; cursor: pointer; font-size: 12px; }
.modern-account-menu__themes button.is-selected { border-color: var(--modern-accent); background: var(--modern-accent-soft); color: var(--modern-accent); font-weight: 700; }
.modern-account-menu__muted, .modern-account-menu__error { margin: 5px 8px; color: var(--modern-muted); font-size: 12px; }
.modern-account-menu__error { color: #d94a61; }
.modern-account-menu__session button { color: #bd2943; }
.modern-topbar__mobile-toggle, .modern-topbar__mobile-navigation { display: none; }
@media (max-width: 1099px) and (min-width: 700px) {
  .modern-primary-navigation__item.is-disabled { display: none; }
  .modern-primary-navigation__more { display: flex; }
}
@media (max-width: 699px) {
  .modern-topbar { grid-template-columns: auto 1fr auto; gap: 10px; padding: 0 max(12px, env(safe-area-inset-right)) 0 max(12px, env(safe-area-inset-left)); }
  .modern-topbar__brand span, .modern-topbar__desktop-navigation { display: none; }
  .modern-topbar__mobile-toggle { display: block; justify-self: start; font-size: 22px; }
  .modern-topbar__mobile-navigation { position: absolute; top: 100%; left: 8px; display: grid; width: min(290px, calc(100vw - 16px)); padding: 8px; border: 1px solid var(--modern-border); border-radius: 12px; background: var(--modern-surface); box-shadow: 0 14px 35px rgba(15,23,42,.18); }
  .modern-topbar__mobile-navigation a, .modern-topbar__mobile-navigation button { display: flex; justify-content: space-between; padding: 12px; border: 0; border-radius: 7px; background: none; color: var(--modern-text); text-decoration: none; text-align: left; }
  .modern-topbar__mobile-navigation button { color: var(--modern-muted); }
  .modern-account-menu { position: fixed; top: calc(var(--modern-shell-header-height) + 6px); right: 6px; left: 6px; width: auto; max-height: calc(100vh - var(--modern-shell-header-height) - 12px); max-height: calc(100dvh - var(--modern-shell-header-height) - 12px); padding-bottom: max(16px, env(safe-area-inset-bottom)); }
}
@supports not (color: color-mix(in srgb, white 50%, black)) { .modern-topbar { background: var(--modern-surface); } }
</style>
